import { defineQuery } from "groq";
import type { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { urlFor } from "@/sanity/lib/image";

export const LOGO_STRIP_QUERY = defineQuery(`
  *[_type == "logoStripSection" && _id == "logoStripSection"][0]{
    heading,
    description,
    logos[]{
      _key,
      alt,
      ...,
      asset->{
        _id,
        url,
        mimeType,
        metadata { dimensions { width, height } }
      }
    }
  }
`);

/** Slot UI — stały box = zero CLS + równa linia bazowa. */
export const LOGO_SLOT = {
    width: 144,
    height: 56,
    /** 2× retina pod max-w slotu */
    cdnWidth: 288,
} as const;

export type LogoStripLogo = {
    _key: string;
    alt: string;
    src: string;
    isSvg: boolean;
};

type LogoStripRawItem = {
    _key?: string;
    alt?: string | null;
    asset?: {
        _id?: string;
        url?: string | null;
        mimeType?: string | null;
        metadata?: {
            dimensions?: { width?: number; height?: number } | null;
        } | null;
    } | null;
};

export type LogoStripDoc = {
    heading?: string | null;
    description?: string | null;
    logos?: Array<LogoStripRawItem | null> | null;
};

function isSvgMime(mimeType?: string | null, url?: string | null): boolean {
    if (mimeType === "image/svg+xml") return true;
    return Boolean(url?.toLowerCase().includes(".svg"));
}

/**
 * Raster → Sanity CDN (AVIF/WebP, resize).
 * SVG → oryginalny URL (bez reencode — Next Image: unoptimized).
 */
function resolveLogoSrc(image: LogoStripRawItem, isSvg: boolean): string | null {
    if (!image?.asset?.url) return null;
    if (isSvg) return image.asset.url;

    try {
        return urlFor(image as SanityImageSource)
            .width(LOGO_SLOT.cdnWidth)
            .fit("max")
            .auto("format")
            .quality(70)
            .url();
    } catch {
        return image.asset.url;
    }
}

export function mapLogoStripLogos(doc: LogoStripDoc | null): LogoStripLogo[] {
    if (!doc?.logos?.length) return [];

    return doc.logos
        .map((item, index) => {
            if (!item) return null;
            const mimeType = item.asset?.mimeType ?? null;
            const rawUrl = item.asset?.url ?? null;
            const isSvg = isSvgMime(mimeType, rawUrl);
            const src = resolveLogoSrc(item, isSvg)?.trim();
            if (!src) return null;

            return {
                _key: item._key ?? `logo-${index}`,
                alt: item.alt?.trim() || "Logo partnera",
                src,
                isSvg,
            } satisfies LogoStripLogo;
        })
        .filter((item): item is LogoStripLogo => item !== null);
}

/** Gęsty tor przy małej liczbie logo — bez dziur w marquee. */
export function densifyLogoStrip(logos: LogoStripLogo[], minItems = 10): LogoStripLogo[] {
    if (logos.length === 0 || logos.length >= minItems) return logos;
    const out: LogoStripLogo[] = [];
    let i = 0;
    while (out.length < minItems) {
        const src = logos[i % logos.length]!;
        out.push({ ...src, _key: `${src._key}-d${out.length}` });
        i += 1;
    }
    return out;
}
